// src/components/UserInput.tsx

import React, { useState } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import clsx from 'clsx'
import { useAuthFetch } from '@/App'



type BreakdownResult = {
  siteContext: string
  pages: Array<{
    title: string
    content: string
    composants: Array<{
      name: string
      description: string
    }>
  }>
  composantsGlobaux: Array<{
    name: string
    description: string
  }>
}

type UserInputProps = {
  onResult?: (result: BreakdownResult) => void
  className?: string
}

const UserInput: React.FC<UserInputProps> = ({ onResult, className = '' }) => {
  const [value, setValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const authFetch = useAuthFetch()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!value.trim()) return
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/breakdown_prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: value }),
      })
      if (!res.ok) {
        const errText = await res.text()
        throw new Error(errText || 'Erreur lors de la génération.')
      }
      const data = await res.json()
      if (onResult) onResult(data)
    } catch (err: any) {
      setError(err?.message || 'Erreur inconnue.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={clsx(
        'card p-4 md:p-6 flex flex-col gap-4 max-w-xl w-full mx-auto',
        className,
      )}
    >
      <label className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-brixel" />
        Décrivez ce que vous souhaitez créer
      </label>
      <textarea
        className="border border-gray-200 rounded px-3 py-2 text-base focus:ring-2 focus:ring-brixel min-h-[80px] resize-vertical"
        placeholder="Ex : Je veux un site pour présenter mon agence de voyage, avec une page d’accueil, une page services, une page contact, etc."
        value={value}
        onChange={e => setValue(e.target.value)}
        disabled={loading}
        maxLength={800}
        required
        aria-label="Décrivez votre besoin"
      />
      <div className="flex gap-2 mt-2">
        <button
          type="submit"
          className={clsx(
            'btn flex-1 flex items-center justify-center gap-2',
            loading && 'opacity-60 cursor-not-allowed',
          )}
          disabled={loading || !value.trim()}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Génération…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Générer le plan du site
            </>
          )}
        </button>
      </div>
      {error && (
        <div className="text-red-500 text-sm mt-2">{error}</div>
      )}
    </form>
  )
}

export default UserInput
